import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Clock, XCircle, LogOut, Award, GraduationCap, HeartHandshake, LayoutGrid, Camera, UserCircle, Compass, HandHeart, Sparkles, ArrowRight, RotateCw, ShieldCheck, Trophy, Download } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, resolveImg, formatApiError, API } from "@/lib/api";
import { useMemberAuth } from "@/context/MemberAuthContext";
import { fmtINR, INTENT_LABEL, MEMBERSHIP_STATUS_LABEL } from "@/lib/membership";
import ProfileForm from "@/pages/membership/ProfileForm";
import PlanSelection from "@/pages/membership/PlanSelection";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";

function SupporterBadge({ isChampion, totalDonated }) {
  const Icon = isChampion ? Trophy : ShieldCheck;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={isChampion ? "champion-badge" : "supporter-badge"}
            className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.1em] cursor-default ${
              isChampion ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-ptf-accent/25 text-ptf-primary border-ptf-primary/15"
            }`}
          >
            <Icon className="h-3 w-3" /> {isChampion ? "Champion" : "Supporter"}
          </span>
        </TooltipTrigger>
        <TooltipContent data-testid="supporter-badge-tooltip">
          Lifetime giving: {fmtINR(totalDonated || 0)}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const STATUS_ICON = { active: CheckCircle2, created: Clock, failed: XCircle, cancelled: XCircle, expired: XCircle, refunded: XCircle };
const STATUS_COLOR = { active: "text-emerald-600", created: "text-amber-600", failed: "text-red-600", cancelled: "text-red-600", expired: "text-red-600", refunded: "text-ptf-text/60" };

const TABS = [
  { key: "profile", label: "Profile", icon: UserCircle },
  { key: "membership", label: "Membership", icon: LayoutGrid },
  { key: "activeChallenges", label: "Active Challenges", icon: Compass },
  { key: "contribution", label: "Your Contribution", icon: HandHeart },
  { key: "training", label: "My Training", icon: GraduationCap },
  { key: "volunteering", label: "My Volunteering", icon: HeartHandshake },
  { key: "certificates", label: "Certificates", icon: Award },
];

export { TABS };

function EmptyRow({ children }) {
  return <p className="text-sm text-ptf-text/55" data-testid="dashboard-empty-row">{children}</p>;
}

function ProfileTab({ member }) {
  const { setMember } = useMemberAuth();
  return (
    <div className="space-y-4">
      {member.is_supporter && (
        <div className="flex items-center gap-3 bg-white border border-ptf-primary/12 p-4" data-testid="profile-supporter-banner">
          <SupporterBadge isChampion={member.is_champion} totalDonated={member.total_donated} />
          <p className="text-xs text-ptf-text/60">
            {member.is_champion
              ? `Thank you for your incredible generosity - you've made ${member.donation_count} donations to Planet Trotter Foundation.`
              : "Thank you for your generosity - you've made a donation to Planet Trotter Foundation."}
          </p>
        </div>
      )}
      <ProfileForm member={member} onSaved={(m) => setMember(m)} mode="edit" />
    </div>
  );
}

function OverviewTab({ memberships, member }) {
  const { refresh } = useMemberAuth();
  const queryClient = useQueryClient();
  const active = memberships.find((m) => m.status === "active");
  const { data: discountSettings } = useQuery({ queryKey: ["training-discount-settings"], queryFn: async () => (await api.get("/training-discount-settings")).data });
  const discountPercent = Number(discountSettings?.percent ?? 25);
  const handleActivated = async () => {
    await refresh();
    queryClient.invalidateQueries({ queryKey: ["my-memberships"] });
  };
  return (
    <>
      <div className="bg-white border border-ptf-primary/12 p-6">
        <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/50">Membership</p>
        {active ? (
          <div className="mt-3" data-testid="active-membership-card">
            {(() => {
              const intent = ["TRAINING", "VOLUNTEER", "BOTH"].includes(active.intent) ? active.intent : "TRAINING";
              return (
                <>
                  <div className="flex flex-wrap items-center gap-4">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <div>
                      <p className="font-display uppercase text-lg tracking-tight text-ptf-primary">{active.plan_name}</p>
                      <p className="text-xs text-ptf-text/55">
                        {INTENT_LABEL[intent]} · Valid until {active.expiry_date}
                        {" · "}
                        <span data-testid="membership-expiry-countdown">
                          {(() => {
                            const daysLeft = Math.ceil((new Date(active.expiry_date) - new Date()) / 86400000);
                            return daysLeft > 0 ? `Expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}` : "Expired";
                          })()}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {(intent === "TRAINING" || intent === "BOTH") && (
                      <Link to="/learning/training" data-testid="explore-training-btn" className="inline-flex items-center gap-2 bg-ptf-primary text-ptf-bg px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] hover:bg-ptf-secondary transition-colors">
                        Explore Training <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    )}
                    {(intent === "VOLUNTEER" || intent === "BOTH") && (
                      <Link to="/volunteer" data-testid="explore-volunteer-btn" className="inline-flex items-center gap-2 border border-ptf-primary/25 text-ptf-primary px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] hover:border-ptf-primary/50 transition-colors">
                        Explore Volunteering <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        ) : (
          <div className="mt-4">
            <p className="text-sm text-ptf-text/60 mb-5" data-testid="no-active-membership">You don't have an active membership yet.</p>
            <PlanSelection member={member} onActivated={handleActivated} />
          </div>
        )}
      </div>

      {active && (
        <div className="bg-white border border-ptf-primary/12 p-6" data-testid="training-benefit-card">
          <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/50">Learning &amp; Training</p>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <div>
              <p className="font-display uppercase text-lg tracking-tight text-ptf-primary">ACTIVE</p>
              <p className="text-xs text-ptf-text/55">Member Training Benefit: {discountPercent}% OFF eligible programmes. Individual and non-transferable.</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-ptf-primary/12 p-6">
        <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/50 mb-4">Payment History</p>
        {memberships.length === 0 ? (
          <EmptyRow>No payments yet.</EmptyRow>
        ) : (
          <div className="space-y-3" data-testid="payment-history">
            {memberships.map((m) => {
              const Icon = STATUS_ICON[m.status] || Clock;
              return (
                <div key={m.id} className="flex items-center justify-between gap-3 border-t border-ptf-primary/8 pt-3 text-sm">
                  <div className="flex items-center gap-2.5">
                    <Icon className={`h-4 w-4 ${STATUS_COLOR[m.status] || "text-ptf-text/60"}`} />
                    <span className="text-ptf-text/80">{m.plan_name}</span>
                  </div>
                  <span className={`text-xs uppercase tracking-[0.1em] ${STATUS_COLOR[m.status] || "text-ptf-text/60"}`}>{MEMBERSHIP_STATUS_LABEL[m.status] || m.status}</span>
                  <span className="text-ptf-text/70">{fmtINR(m.total_amount)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

const REG_STATUS_COLOR = { confirmed: "text-emerald-600", received: "text-amber-600", payment_pending: "text-amber-600", paid_capacity_conflict: "text-amber-600", cancelled: "text-red-600", accepted: "text-emerald-600", declined: "text-red-600" };

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

function TrainingTab() {
  const { data = [], isLoading } = useQuery({ queryKey: ["my-training-registrations"], queryFn: async () => (await api.get("/member/training-registrations")).data });
  if (isLoading) return <EmptyRow>Loading...</EmptyRow>;
  if (data.length === 0) return <div className="bg-white border border-ptf-primary/12 p-6"><EmptyRow>You haven't registered for any Training programme yet. Browse the Learn &amp; Train catalogue to apply.</EmptyRow></div>;
  return (
    <div className="bg-white border border-ptf-primary/12 p-6" data-testid="my-training-list">
      <div className="space-y-3">
        {data.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-3 border-t border-ptf-primary/8 first:border-t-0 first:pt-0 pt-3 text-sm">
            <div>
              <p className="text-ptf-text/85 font-medium">{r.programme_title}</p>
              <p className="text-xs text-ptf-text/50">{fmtDate(r.created_at)}{r.preferred_date ? ` · ${r.preferred_date}` : ""}</p>
            </div>
            <span className={`text-xs uppercase tracking-[0.1em] ${REG_STATUS_COLOR[r.status] || "text-ptf-text/60"}`}>{(r.status || "").replace(/_/g, " ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const CHALLENGE_STATUS_TONE = {
  Submitted: "text-amber-600", Shortlisted: "text-ptf-secondary", "Interview Scheduled": "text-ptf-secondary",
  "Interview Completed": "text-ptf-secondary", Selected: "text-emerald-600", "Not Selected": "text-ptf-text/50",
};

function challengeCta(app) {
  if (!app || app.status === "Draft") {
    return { label: app?.payment_status === "paid" ? "Continue Application" : "Apply Now", action: true };
  }
  if (app.status === "Paid") return { label: "Continue Application", action: true };
  if (app.status === "Submitted") return { label: "Application Submitted", action: false };
  if (app.status === "Shortlisted") return { label: "Shortlisted", action: false };
  if (["Interview Scheduled", "Interview Completed"].includes(app.status)) return { label: "Interview Stage", action: false };
  return { label: app.status, action: false };
}

function ChallengeCard({ icon: Icon, title, feeLabel, app, isLoading, applyTo, dates }) {
  if (isLoading) return <div className="bg-white border border-ptf-primary/12 p-6"><EmptyRow>Loading...</EmptyRow></div>;
  const cta = challengeCta(app);
  return (
    <div className="bg-white border border-ptf-primary/12 p-6" data-testid={`challenge-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-ptf-secondary shrink-0" />
          <div>
            <p className="font-display uppercase text-lg tracking-tight text-ptf-primary">{title}</p>
            <p className="text-xs text-ptf-text/50">{dates}</p>
          </div>
        </div>
        <span className={`text-[0.65rem] uppercase tracking-[0.1em] font-semibold ${CHALLENGE_STATUS_TONE[app?.status] || "text-ptf-text/60"}`} data-testid="challenge-status-badge">
          {app?.status && app.status !== "Draft" ? app.status : ""}
        </span>
      </div>
      <p className="mt-3 text-xs text-ptf-text/55">{feeLabel}</p>
      {cta.action ? (
        <Link to={applyTo} data-testid="challenge-cta" className="mt-4 inline-flex items-center gap-2 bg-ptf-primary text-ptf-bg px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] hover:bg-ptf-secondary transition-colors">
          {cta.label} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      ) : (
        <p className="mt-4 text-sm font-semibold text-ptf-primary" data-testid="challenge-cta">{cta.label}</p>
      )}
      {app?.membership_granted && (
        <p className="mt-3 text-xs text-emerald-700 border-t border-ptf-primary/8 pt-3">
          Your 1-year Planet Trotter Foundation membership was activated as part of this application. See benefits under Membership.
        </p>
      )}
    </div>
  );
}

function ActiveChallengesTab() {
  const { data: dawnApp, isLoading: dawnLoading } = useQuery({ queryKey: ["dawn-application"], queryFn: async () => (await api.get("/member/dawn/application")).data });
  const { data: lhApp, isLoading: lhLoading } = useQuery({ queryKey: ["last-horizon-application"], queryFn: async () => (await api.get("/member/last-horizon/application")).data });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="active-challenges-grid">
      <ChallengeCard icon={Sparkles} title="DAWN: A Project of Belief" feeLabel="₹499 Application Fee (non-refundable)" app={dawnApp} isLoading={dawnLoading} applyTo="/dawn/apply" dates="27 February to 13 March 2027 · Ladhiya Valley, Uttarakhand" />
      <ChallengeCard icon={Camera} title="The Last Horizon" feeLabel="₹5,000 Application Fee" app={lhApp} isLoading={lhLoading} applyTo="/last-horizon/apply" dates="10-30 October 2026 · Kaziranga National Park & Tanzania" />
    </div>
  );
}

function MyDonations() {
  const { data: donations = [], isLoading } = useQuery({ queryKey: ["my-donations"], queryFn: async () => (await api.get("/member/donations")).data });
  const resendReceipt = useMutation({
    mutationFn: (id) => api.post(`/member/donations/${id}/resend-receipt`),
    onSuccess: (res) => toast.success(res.data?.message || "Receipt resent to your email."),
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  if (isLoading) return <EmptyRow>Loading...</EmptyRow>;
  if (donations.length === 0) return null;
  return (
    <div className="bg-white border border-ptf-primary/12 p-6" data-testid="my-donations-list">
      <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/50 mb-4">My Donations</p>
      <div className="space-y-3">
        {donations.map((d) => (
          <div key={d.id} className="flex items-center justify-between gap-3 border-t border-ptf-primary/8 first:border-t-0 first:pt-0 pt-3 text-sm">
            <div>
              <p className="text-ptf-text/85 font-medium">{d.programme_name || "General Donation"} · {fmtINR(d.amount)}</p>
              <p className="text-xs text-ptf-text/50">{fmtDate(d.created_at)}{d.receipt_number ? ` · ${d.receipt_number}` : ""}</p>
            </div>
            {d.has_receipt ? (
              <div className="flex items-center gap-2">
                <a
                  href={`${API}/member/donations/${d.id}/receipt`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`download-my-receipt-${d.id}`}
                  className="inline-flex items-center gap-1.5 border border-ptf-primary/15 px-3 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-ptf-primary hover:bg-ptf-accent/20 transition-colors"
                >
                  <Download className="h-3 w-3" /> Download
                </a>
                <button
                  onClick={() => resendReceipt.mutate(d.id)}
                  disabled={resendReceipt.isPending}
                  data-testid={`resend-my-receipt-${d.id}`}
                  className="inline-flex items-center gap-1.5 border border-ptf-primary/15 px-3 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-ptf-primary hover:bg-ptf-accent/20 transition-colors disabled:opacity-50"
                >
                  <RotateCw className="h-3 w-3" /> Resend Receipt
                </button>
              </div>
            ) : (
              <span className="text-xs text-ptf-text/40">No receipt issued</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ContributionTab() {
  const { data: programmes = [], isLoading } = useQuery({ queryKey: ["programmes"], queryFn: async () => (await api.get("/programmes")).data });
  const eligible = programmes.filter((p) => p.donations_enabled);
  return (
    <div className="space-y-8">
      <MyDonations />
      {isLoading ? (
        <EmptyRow>Loading...</EmptyRow>
      ) : eligible.length === 0 ? (
        <div className="bg-white border border-ptf-primary/12 p-6"><EmptyRow>No programmes are currently accepting contributions. Please check back soon.</EmptyRow></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="contribution-programmes-grid">
          {eligible.map((p) => (
            <div key={p.id} className="bg-white border border-ptf-primary/12 overflow-hidden flex flex-col" data-testid={`contribution-card-${p.id}`}>
              {p.images?.[0] && <img src={resolveImg(p.images[0])} alt={p.name} className="h-36 w-full object-cover" />}
              <div className="p-5 flex flex-col flex-1">
                <p className="font-display uppercase text-lg tracking-tight text-ptf-primary">{p.name}</p>
                {p.short_description && <p className="mt-2 text-xs text-ptf-text/60 leading-relaxed flex-1">{p.short_description}</p>}
                <div className="mt-4 flex items-center gap-4">
                  <Link to={`/programmes/${p.slug || p.id}`} data-testid={`contribution-read-more-${p.id}`} className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-primary underline underline-offset-4 hover:text-ptf-secondary">Read More</Link>
                  <Link to={`/programmes/${p.slug || p.id}`} data-testid={`contribution-donate-now-${p.id}`} className="text-[0.65rem] uppercase tracking-[0.14em] bg-ptf-primary text-ptf-bg px-4 py-2 hover:bg-ptf-secondary transition-colors">Donate Now</Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VolunteeringTab() {
  const { data = [], isLoading } = useQuery({ queryKey: ["my-volunteer-applications"], queryFn: async () => (await api.get("/member/volunteer-applications")).data });
  if (isLoading) return <EmptyRow>Loading...</EmptyRow>;
  if (data.length === 0) return <div className="bg-white border border-ptf-primary/12 p-6"><EmptyRow>You haven't submitted a Volunteer application yet. Apply from the Volunteer page.</EmptyRow></div>;
  return (
    <div className="bg-white border border-ptf-primary/12 p-6" data-testid="my-volunteer-list">
      <div className="space-y-3">
        {data.map((a) => (
          <div key={a.id} className="flex items-center justify-between gap-3 border-t border-ptf-primary/8 first:border-t-0 first:pt-0 pt-3 text-sm">
            <div>
              <p className="text-ptf-text/85 font-medium">Volunteer Application</p>
              <p className="text-xs text-ptf-text/50">{(a.areas || []).join(", ") || "General volunteering"} · {fmtDate(a.created_at)}</p>
            </div>
            <span className={`text-xs uppercase tracking-[0.1em] ${REG_STATUS_COLOR[a.status] || "text-ptf-text/60"}`}>{(a.status || "").replace(/_/g, " ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CertificatesTab() {
  const { data = [], isLoading } = useQuery({ queryKey: ["my-certificates"], queryFn: async () => (await api.get("/member/certificates")).data });
  if (isLoading) return <EmptyRow>Loading...</EmptyRow>;
  if (data.length === 0) return <div className="bg-white border border-ptf-primary/12 p-6"><EmptyRow>No certificates issued to your account yet. Certificates appear here once a Training or Volunteering programme is completed.</EmptyRow></div>;
  return (
    <div className="bg-white border border-ptf-primary/12 p-6" data-testid="my-certificates-list">
      <div className="space-y-3">
        {data.map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-3 border-t border-ptf-primary/8 first:border-t-0 first:pt-0 pt-3 text-sm">
            <div>
              <p className="text-ptf-text/85 font-medium">{c.programme}</p>
              <p className="text-xs text-ptf-text/50">{c.cert_number} · {c.certificate_type}</p>
            </div>
            <span className={`text-xs uppercase tracking-[0.1em] ${c.status === "VALID" ? "text-emerald-600" : "text-red-600"}`}>{c.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MemberDashboard({ member }) {
  const { logout } = useMemberAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const urlTab = searchParams.get("tab");
    return TABS.some((t) => t.key === urlTab) ? urlTab : "profile";
  });
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab && TABS.some((t) => t.key === urlTab) && urlTab !== tab) setTab(urlTab);
  }, [searchParams]);
  const { data: memberships = [] } = useQuery({
    queryKey: ["my-memberships"],
    queryFn: async () => (await api.get("/member/memberships")).data,
  });

  return (
    <div data-testid="member-dashboard" className="space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          {member.profile_photo?.id ? (
            <img src={resolveImg(member.profile_photo.url)} alt={member.name} data-testid="dashboard-profile-photo" className="h-14 w-14 rounded-full object-cover border border-ptf-primary/15" />
          ) : (
            <div className="h-14 w-14 rounded-full bg-ptf-primary/8 grid place-items-center text-ptf-primary/40 font-display text-lg uppercase">{(member.name || "M").charAt(0)}</div>
          )}
          <div>
            <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/50 mb-1">My Planet Trotter Dashboard</p>
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="font-display uppercase text-3xl tracking-tight text-ptf-primary">{member.name || "Member"}</h2>
              {member.is_supporter && <SupporterBadge isChampion={member.is_champion} totalDonated={member.total_donated} />}
            </div>
            <p className="mt-1 text-sm text-ptf-text/55">{member.member_number} · {member.email}</p>
          </div>
        </div>
        <button type="button" data-testid="member-logout" onClick={logout} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-ptf-text/60 hover:text-ptf-primary transition-colors">
          <LogOut className="h-3.5 w-3.5" />Log Out
        </button>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-ptf-primary/10 pb-1" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            data-testid={`dashboard-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-[0.65rem] font-semibold uppercase tracking-[0.12em] transition-colors ${
              tab === t.key ? "bg-ptf-primary text-ptf-bg" : "text-ptf-text/60 hover:text-ptf-primary"
            }`}
          >
            <t.icon className="h-3.5 w-3.5" />{t.label}
          </button>
        ))}
      </div>

      <div className="space-y-6" data-testid={`dashboard-panel-${tab}`}>
        {tab === "profile" && <ProfileTab member={member} />}
        {tab === "membership" && <OverviewTab memberships={memberships} member={member} />}
        {tab === "activeChallenges" && <ActiveChallengesTab />}
        {tab === "contribution" && <ContributionTab />}
        {tab === "training" && <TrainingTab />}
        {tab === "volunteering" && <VolunteeringTab />}
        {tab === "certificates" && <CertificatesTab />}
      </div>

      <div className="bg-ptf-primary text-ptf-bg p-6 grain">
        <p className="text-sm leading-relaxed text-ptf-bg/85">
          Applying for Training or Volunteering now uses this membership automatically -
          just fill the application on the programme or Volunteer page while signed in.
        </p>
      </div>
    </div>
  );
}
